import { useEffect, useRef, useState } from "react";
import { useMember } from "@/context/MemberContext";
import { isIOSApp, openOnWebsite } from "@/lib/platform";
import { motion, AnimatePresence } from "framer-motion";
import { MessageCircle, X, Send, Sparkles } from "lucide-react";
import { API } from "@/lib/api";

const GREETING = {
  role: "assistant",
  content:
    "Welcome to THE ZO POUND EXPERIENCE. Ask me anything about the show, Ali Zo, the movement, or Haitian history and contributions.",
};

function getSessionId() {
  let id = localStorage.getItem("zp_chat_session");
  if (!id) {
    id = `sess-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
    localStorage.setItem("zp_chat_session", id);
  }
  return id;
}

export default function ChatHost() {
  const [open, setOpen] = useState(false);
  const { member, setPaywall, setLoginOpen } = useMember();
  const [messages, setMessages] = useState([GREETING]);
  const [input, setInput] = useState("");
  const [thinking, setThinking] = useState(false);
  const bottomRef = useRef(null);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, open]);

  const send = async (e) => {
    e.preventDefault();
    const text = input.trim();
    if (!text || thinking) return;
    setInput("");
    setThinking(true);
    setMessages((m) => [...m, { role: "user", content: text }, { role: "assistant", content: "" }]);

    try {
      const res = await fetch(`${API}/ai/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ message: text, session_id: getSessionId() }),
      });
      if (!res.ok) throw new Error("Chat unavailable");
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buf = "";
      let done = false;
      while (!done) {
        const { done: rd, value } = await reader.read();
        if (rd) break;
        buf += decoder.decode(value, { stream: true });
        const parts = buf.split("\n\n");
        buf = parts.pop();
        for (const part of parts) {
          const line = part.trim();
          if (!line.startsWith("data:")) continue;
          const payload = line.slice(5).trim();
          if (payload === "[DONE]") {
            done = true;
            break;
          }
          try {
            const token = JSON.parse(payload);
            setMessages((m) => {
              const copy = [...m];
              copy[copy.length - 1] = { role: "assistant", content: copy[copy.length - 1].content + token };
              return copy;
            });
          } catch (err) { /* partial json, skip */ }
        }
      }
    } catch (err) {
      setMessages((m) => {
        const copy = [...m];
        copy[copy.length - 1] = {
          role: "assistant",
          content: "Signal lost — try again, or reach us at simplicityfilmsllc@gmail.com.",
        };
        return copy;
      });
    } finally {
      setThinking(false);
    }
  };

  return (
    <>
      <motion.button
        initial={{ opacity: 0, scale: 0.8 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ delay: 2, duration: 0.5 }}
        data-testid="chat-host-button"
        onClick={() => setOpen(true)}
        className={`fixed bottom-6 right-6 z-[65] flex items-center gap-2 bg-blood hover:bg-red-700 text-white font-display font-bold uppercase tracking-wider px-5 py-3 shadow-lg shadow-blood/30 transition-colors ${open ? "hidden" : ""}`}
      >
        <MessageCircle size={18} /> Ask the Movement
      </motion.button>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 40, scale: 0.97 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 40, scale: 0.97 }}
            transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
            data-testid="chat-host-panel"
            className="fixed bottom-6 right-6 z-[65] w-[calc(100vw-3rem)] max-w-md bg-elevate border border-white/15 clip-frame flex flex-col overflow-hidden"
            style={{ height: "min(560px, 75vh)" }}
          >
            <div className="flex items-center justify-between px-5 py-4 border-b border-white/10 bg-surface">
              <div className="flex items-center gap-2">
                <Sparkles size={16} className="text-blood" />
                <div>
                  <p className="font-display font-bold uppercase tracking-wider text-sm">Ask the Movement</p>
                  <p className="font-mono text-[10px] uppercase tracking-widest text-gray-500">AI Host · Powered by Gemini</p>
                </div>
              </div>
              <button
                data-testid="chat-host-close-button"
                onClick={() => setOpen(false)}
                className="text-gray-400 hover:text-blood transition-colors"
                aria-label="Close chat"
              >
                <X size={20} />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto p-4 space-y-3" data-testid="chat-host-messages">
              {messages.map((m, i) => (
                <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                  <div
                    data-testid={`chat-message-${i}`}
                    className={`max-w-[85%] px-4 py-2.5 text-sm leading-relaxed ${
                      m.role === "user"
                        ? "bg-blood text-white"
                        : "bg-surface border border-white/10 text-gray-200"
                    }`}
                  >
                    {m.content || (thinking && i === messages.length - 1 ? "…" : "")}
                  </div>
                </div>
              ))}
              <div ref={bottomRef} />
            </div>

            {!member ? (
              <div data-testid="chat-host-locked" className="p-4 border-t border-white/10 bg-surface space-y-3">
                <p className="font-mono text-[11px] uppercase tracking-widest text-blood">Live chat is for Insiders</p>
                <p className="text-sm text-gray-400">Join for $1.99/month to talk with the movement and unlock every episode.</p>
                <div className="flex gap-2">
                  <button data-testid="chat-host-join-button" onClick={() => (isIOSApp() ? openOnWebsite("/#contact") : setPaywall({ title: "the live chat" }))}
                    className="flex-1 bg-blood hover:bg-red-700 text-white font-display font-bold uppercase tracking-wider text-sm py-2.5 transition-colors">Join — $1.99/mo</button>
                  <button data-testid="chat-host-login-button" onClick={() => setLoginOpen(true)}
                    className="px-4 border border-white/20 hover:border-blood font-mono text-xs uppercase tracking-widest transition-colors">Log in</button>
                </div>
              </div>
            ) : (
            <form onSubmit={send} className="flex gap-2 p-3 border-t border-white/10 bg-surface">
              <input
                data-testid="chat-host-input"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="Ask about Ali Zo, the movement, Haiti…"
                className="flex-1 bg-ink border border-white/15 px-3 py-2.5 text-sm focus:outline-none focus:border-blood"
              />
              <button
                data-testid="chat-host-send-button"
                type="submit"
                disabled={thinking || !input.trim()}
                className="bg-blood hover:bg-red-700 disabled:opacity-40 px-4 transition-colors"
                aria-label="Send"
              >
                <Send size={16} />
              </button>
            </form>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
